"use client";

import { Typography } from "@mui/material";
import React, { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import styled, { css, keyframes } from "styled-components";

// Types for button props
export type ButtonVariant = "lime" | "lightPurple";
export type ButtonState =
  | "idle"
  | "hover"
  | "pressed"
  | "focus"
  | "loading"
  | "disabled";

// Props interface for the Button component
export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "as"> {
  variant?: ButtonVariant;
  state?: ButtonState;
  isLoading?: boolean;
  isDisabled?: boolean;
  href?: string;
  as?: React.ElementType;
  children: ReactNode;
}

// Spinner animation for loading state
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Base button styles
const baseButtonStyles = css<{ $variant: ButtonVariant; $state: ButtonState }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  padding: 0 40px;
  border-radius: 14px;
  font-family:
    Manrope,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;

  @media (max-width: 991px) {
    white-space: initial;
  }

  // Variant and state specific styles
  ${({ $variant, $state }) => {
    // Lime variant styles
    if ($variant === "lime") {
      switch ($state) {
        case "idle":
          return css`
            background-color: #defb1e;
            color: #321338;
          `;
        case "hover":
          return css`
            background-color: #c5e200;
            color: #321338;
          `;
        case "pressed":
          return css`
            background-color: #819400;
            color: #321338;
          `;
        case "focus":
          return css`
            background-color: #defb1e;
            color: #321338;
            border: 4px solid #dbdeff;
          `;
        case "loading":
          return css`
            background-color: #defb1e;
            color: #321338;
          `;
        case "disabled":
          return css`
            background-color: #f1f1f1;
            color: #9e9e9e;
            cursor: not-allowed;
          `;
        default:
          return css`
            background-color: #defb1e;
            color: #321338;
          `;
      }
    }
    // Light purple variant styles
    else {
      switch ($state) {
        case "idle":
          return css`
            background-color: #cdbcf3;
            color: #321338;
          `;
        case "hover":
          return css`
            background-color: #ded4f6;
            color: #321338;
          `;
        case "pressed":
          return css`
            background-color: #9584ba;
            color: #321338;
          `;
        case "focus":
          return css`
            background-color: #cdbcf3;
            color: #321338;
            border: 4px solid #dbdeff;
          `;
        case "loading":
          return css`
            background-color: #cdbcf3;
            color: #321338;
          `;
        case "disabled":
          return css`
            background-color: #f1f1f1;
            color: #9e9e9e;
            cursor: not-allowed;
          `;
        default:
          return css`
            background-color: #cdbcf3;
            color: #321338;
          `;
      }
    }
  }}

  &:hover {
    ${({ $state, $variant }) => {
      if ($state === "disabled" || $state === "loading") return "";

      if ($variant === "lime") {
        return css`
          background-color: #c5e200;
        `;
      } else {
        return css`
          background-color: #ded4f6;
        `;
      }
    }}
  }

  &:active {
    ${({ $state, $variant }) => {
      if ($state === "disabled" || $state === "loading") return "";

      if ($variant === "lime") {
        return css`
          background-color: #819400;
        `;
      } else {
        return css`
          background-color: #9584ba;
        `;
      }
    }}
  }

  &:focus-visible {
    ${({ $state, $variant }) => {
      if ($state === "disabled") return "";

      if ($variant === "lime") {
        return css`
          background-color: #defb1e;
          border: 4px solid #dbdeff;
          outline: none;
        `;
      } else {
        return css`
          background-color: #cdbcf3;
          border: 4px solid #dbdeff;
          outline: none;
        `;
      }
    }}
  }
`;

// Styled button component
const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $state: ButtonState;
}>`
  ${baseButtonStyles}
`;

// Styled link component
const StyledLink = styled.a<{ $variant: ButtonVariant; $state: ButtonState }>`
  ${baseButtonStyles}
  text-decoration: none;
`;

// Spinner component for loading state
const SpinnerIcon = styled.div`
  width: 24px;
  height: 24px;
  animation: ${rotate} 1s linear infinite;

  svg {
    width: 100%;
    height: 100%;
  }
`;

// Spinner SVG for lime variant
const LimeSpinner = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
      fill="#321338"
      fillOpacity="0.3"
    />
    <path
      d="M12 2V4C16.41 4 20 7.59 20 12H22C22 6.48 17.52 2 12 2Z"
      fill="#321338"
    />
  </svg>
);

// Spinner SVG for light purple variant
const LightPurpleSpinner = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
      fill="#321338"
      fillOpacity="0.3"
    />
    <path
      d="M12 2V4C16.41 4 20 7.59 20 12H22C22 6.48 17.52 2 12 2Z"
      fill="#321338"
    />
  </svg>
);

// Button component
export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      variant = "lime",
      state = "idle",
      isLoading = false,
      isDisabled = false,
      href,
      as,
      children,
      ...props
    },
    ref,
  ) => {
    // Determine the current state of the button
    const currentState: ButtonState = isDisabled
      ? "disabled"
      : isLoading
        ? "loading"
        : state;

    // If the button is a link (has href or as prop)
    if (href || as) {
      return (
        <StyledLink
          ref={ref as React.Ref<HTMLAnchorElement>}
          $variant={variant}
          $state={currentState}
          href={isDisabled ? undefined : href}
          as={as}
          tabIndex={isDisabled ? -1 : 0}
          aria-disabled={isDisabled}
          {...props}
        >
          {isLoading ? (
            <SpinnerIcon>
              {variant === "lime" ? <LimeSpinner /> : <LightPurpleSpinner />}
            </SpinnerIcon>
          ) : (
            <Typography variant="body7" sx={{
                color: "darkPurple.500",
            }}>
                {children}
            </Typography>
          )}
        </StyledLink>
      );
    }

    // Regular button
    return (
      <StyledButton
        ref={ref as React.Ref<HTMLButtonElement>}
        $variant={variant}
        $state={currentState}
        disabled={isDisabled}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading ? (
          <SpinnerIcon>
            {variant === "lime" ? <LimeSpinner /> : <LightPurpleSpinner />}
          </SpinnerIcon>
        ) : (
          children
        )}
      </StyledButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
